/**
 * Custom override of getUpdatedHandles to allow for snapping handles.
 * Most code is copied from the original getUpdatedHandles function.
 */

function getUpdatedHandles(
  handles,
  updateKey,
  updateValue,
  reversed = false
) {
  const index = handles.findIndex(v => v.key === updateKey);

  if (index !== -1) {
    const { key, val } = handles[index];

    if (val === updateValue) {
      return handles;
    }

    return [
      ...handles.slice(0, index),
      { key, val: updateValue },
      ...handles.slice(index + 1),
    ].sort(getSortByVal(reversed));
  }

  return handles;
}

function getSortByVal(reversed = false) {
  return function sortByVal(a, b) {
    if (a.val > b.val) {
      return reversed ? -1 : 1;
    }

    if (b.val > a.val) {
      return reversed ? 1 : -1;
    }

    return 0;
  };
}

export function handleMove(
  curr,
  next,
  step,
  reversed,
  getValue,
  maxLength
) {
  let indexForMovingHandle = -1;
  let handleMoveIsPositive = true;

  for (let i = 0; i < curr.length; i++) {
    const c = curr[i];
    const n = next[i];

    // make sure keys are in same order if not return curr
    if (!n || n.key !== c.key) {
      return curr;
    } else if (n.val !== c.val) {
      indexForMovingHandle = i;
      handleMoveIsPositive = n.val - c.val > 0;
    }
  }

  // nothing has changed (shouldn't happen but just in case).
  if (indexForMovingHandle === -1) {
    return curr;
  } else {
    const increment = handleMoveIsPositive ? step : -step;
    for (let i = 0; i < next.length; i++) {
      const n0 = next[i];
      const n1 = next[i + 1];
      if (n1 && n0.val === n1.val) {
        if (i === indexForMovingHandle) {
          const left = next[0].val;
          const right = next[1].val;
          const span = right - left;

          // If span moved less than 2 steps, move the block that many steps
          if ( span > maxLength && indexForMovingHandle === 1) {
            const stepCount = (next[1].val - curr[1].val) / step;
            if (stepCount < 2) {
              const rightClamped = [{ ...next[0], val: getValue(curr[0].val + (step * stepCount)) }, { ...next[1], val: getValue(curr[1].val + (step * stepCount)) }];
              return rightClamped;
            }
          }
          
          // If more than max length to the right, snap the whole block to the cursor
          if (maxLength && span > maxLength && indexForMovingHandle === 1) {
            const clampVal = right - maxLength - span;

            const rightClamped = [{ ...next[0], val: getValue(next[1].val) }, { ...next[1], val: getValue(next[1].val + oldSpan) }];
            return rightClamped;
          }

          // If more than max length to the left, move the left back and snap the right to left + maxLength
          if (maxLength && span > maxLength && indexForMovingHandle !== 1) {
            const leftClamped = next.map(h =>
              h.key === next[1 - indexForMovingHandle].key
                ? { ...h, val: getValue(left + maxLength) }
                : h
            );

            return leftClamped;
          }

          const newStep = n1.val + increment;
          if (getValue(newStep) === newStep) {
            const clone = getUpdatedHandles(
              next,
              n1.key,
              n1.val + increment,
              reversed
            );
            const check = handleMove(next, clone, step, reversed, getValue, maxLength);
            if (check === next) {
              return curr;
            } else {
              return check;
            }
          } else {
            return curr;
          }
        } else {
          const newStep = n0.val + increment;
          if (getValue(newStep) === newStep) {
            const clone = getUpdatedHandles(
              next,
              n0.key,
              n0.val + increment,
              reversed
            );
            const check = handleMove(next, clone, step, reversed, getValue, maxLength);

            if (check === next) {
              return curr;
            } else {
              return check;
            }
          } else {
            return curr;
          }
        }
      }
    }
  }

  // Never let a user drag handle to the past
  next[0].val = next[0].val > Date.now() ? next[0].val : curr[0].val;
  next[1].val = next[1].val === curr[0].val ? next[1].val + step : next[1].val;

  const left = next[0].val;
  const right = next[1].val;
  const span = right - left;
  const oldSpan = curr[1].val - curr[0].val;

  // If span moved less than 2 steps, move the block that many steps
  if ( span > maxLength && indexForMovingHandle === 1) {
    const stepCount = (next[1].val - curr[1].val) / step;
    if (stepCount < 2) {
      const rightClamped = [{ ...next[0], val: getValue(curr[0].val + (step * stepCount)) }, { ...next[1], val: getValue(curr[1].val + (step * stepCount)) }];
      return rightClamped;
    }
  }

  // If more than max length to the right, snap the whole block to the cursor
  if (maxLength && span > maxLength && indexForMovingHandle === 1) { 
    const rightClamped = [{ ...next[0], val: getValue(next[1].val) }, { ...next[1], val: getValue(next[1].val + oldSpan) }];
    return rightClamped;
  }

  // If more than max length to the left, move the left back and snap the right to left + maxLength
  if (maxLength && span > maxLength && indexForMovingHandle !== 1) {
    const leftClamped = next.map(h =>
      h.key === next[1 - indexForMovingHandle].key
        ? { ...h, val: getValue(left + maxLength) }
        : h
    );

    return leftClamped;
  }

  return next;
}